import DUIField from "@/components/common/dui-field";
import SelectField from "@/components/common/select";
import TextField from "@/components/common/text-field";
import { useHeaderButton } from "@/contexts/header-button-context";
import { useUpdateProfile } from "@/queries/me";
import {
  type UpdateProfileFormData,
  schema,
} from "@/schemas/profile/update-profile";
import { Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { InternationalInputPhone } from "../../components/international-input-phone";

interface Props {
  id: string;
  data: {
    name: string;
    last_name: string;
    dui: string;
    email: string;
    phone_number?: {
      number: string;
      calling_code: string;
      country: string;
    };
    department_id: string;
    registered_position_id: string;
    assigned_institution_id: string;
  };
}

const ProfileForm = ({ data }: Props) => {
  const { mutateAsync, isPending: isLoading } = useUpdateProfile();
  const { setComp } = useHeaderButton();
  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<UpdateProfileFormData>({
    resolver: zodResolver(schema),
    reValidateMode: "onChange",
    defaultValues: {
      phone: {
        number: data?.phone_number?.number || "",
        calling_code: data?.phone_number?.calling_code || "503",
        country: data?.phone_number?.country || "SV",
      },
    },
  });

  const phone = watch("phone");
  const phoneHasChanged = useMemo(() => {
    if (!data?.phone_number) return true;

    const currentPhone = phone?.number?.replace(/\s/g, "") ?? "";
    const originalPhone = data.phone_number.number.replace(/\s/g, "");

    return (
      currentPhone !== originalPhone ||
      phone?.country !== "SV" ||
      phone?.calling_code !== "503"
    );
  }, [phone, data]);

  useEffect(() => {
    setComp(
      <Button
        variant="default"
        size="xs"
        form="profile-form"
        type="submit"
        disabled={!isValid || !phoneHasChanged}
      >
        Guardar cambios
        {isLoading ? (
          <Icon
            type="far"
            name="spinner-third"
            className="animate-spin text-current"
          />
        ) : (
          <Icon type="far" name="save" className="text-current" />
        )}
      </Button>,
    );
    return () => {
      setComp(null);
    };
  }, [setComp, isValid, phoneHasChanged, isLoading]);

  const onSubmit = async (data: UpdateProfileFormData) => {
    try {
      await mutateAsync({
        phone_number: data.phone,
      });
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={handleSubmit(onSubmit)}
      id="profile-form"
    >
      <div className="flex flex-col gap-4 @container">
        <Heading variant="h5">Información personal</Heading>
        <div className="flex flex-col gap-4">
          <TextField
            id="first-name"
            label="Nombres"
            placeholder="Nombres"
            value={data.name || ""}
            required
            disabled
          />
          <TextField
            id="last-name"
            label="Apellidos"
            placeholder="Apellidos"
            value={data.last_name || ""}
            required
            disabled
          />
          <DUIField
            id="dui-number"
            label="Número de DUI"
            placeholder="00000000-0"
            value={data.dui || ""}
            required
            disabled
          />
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-4 @container">
        <Heading variant="h5">Información de contacto</Heading>
        <div className="flex flex-col gap-4">
          <TextField
            id="email"
            label="Correo electrónico"
            placeholder="Correo electrónico"
            value={data.email || ""}
            required
            disabled
          />
          <Controller
            name="phone"
            control={control}
            render={({ field: { ref, ...rest } }) => {
              return (
                <InternationalInputPhone
                  {...rest}
                  inputRef={ref}
                  error={errors.phone?.number?.message}
                />
              );
            }}
          />
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-4 @container">
        <Heading variant="h5">Otra información</Heading>
        <div className="flex flex-col gap-4">
          <SelectField
            label="Institución asignada"
            placeholder="Institución asignada"
            name="assigned_institution_id"
            required
            value={data.assigned_institution_id || "1-1"}
            options={[
              {
                value: "1-1",
                label: "Gobierno de El Salvador",
              },
            ]}
            onChange={() => {}}
            isLoading={false}
            isDisabled
          />
          <SelectField
            label="Unidad o departamento"
            placeholder="Unidad o departamento"
            name="department_id"
            required
            value={data.department_id || "1-2"}
            options={[
              {
                value: "1-2",
                label: "Gestión de Proyectos",
              },
            ]}
            onChange={() => {}}
            isLoading={false}
            isDisabled
          />
          <SelectField
            label="Cargo registrado"
            placeholder="Cargo registrado"
            name="registered_position_id"
            required
            value={data.registered_position_id || "1-3"}
            options={[
              {
                value: "1-3",
                label: "Gerente de proyectos",
              },
            ]}
            onChange={() => {}}
            isLoading={false}
            isDisabled
          />
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-4 @container">
        <Heading variant="h5">Configuración</Heading>
        <div className="flex flex-col gap-4">
          <SelectField
            label="Rol asignado"
            placeholder="Rol asignado"
            name="timezone"
            required
            value={"1-4"}
            options={[
              {
                label: "Administrador",
                value: "1-4",
              },
            ]}
            onChange={() => {}}
            isLoading={false}
            isDisabled
          />
        </div>
      </div>
    </form>
  );
};

export default ProfileForm;
